/**
 * What the plan cards under a package answer write to `activities`, so we can see how often
 * Alfie shows plans and which ones get clicked.
 *
 * Every row is `type: 'alfie'`, `entity_type: 'plan_card'`, keyed on the plan code. It sits
 * beside `alfie-widget/widget-analytics.ts` rather than in `lib/tracking/coaching-analytics.ts`
 * because `admin_analytics()` counts every `type: 'coaching'` row as coaching activity, and a
 * plan card is not one.
 *
 * Plan code and where it was shown only: never the member's email, which rides the link.
 */
import { type Activity, api } from '@/api/client';
import type { PlanAction } from '@/api/alfie';

export type PlanCardSurface = 'alfie_page' | 'widget';

export type PlanCardEvent =
  | { action: 'plan_card_shown'; code: string; surface: PlanCardSurface }
  | {
    action: 'plan_card_clicked';
    code: string;
    surface: PlanCardSurface;
    planAction: PlanAction;
    /** `button` on the card, or the blue `text_link` under the cards. */
    via: 'button' | 'text_link';
  };

export type PlanCardActivity = Omit<Activity, 'id' | 'user_id' | 'created_at'>;

const DESCRIPTIONS: Record<PlanCardEvent['action'], string> = {
  plan_card_shown: 'Alfie showed a plan card',
  plan_card_clicked: 'Clicked a plan card in Alfie',
};

/** The `activities` row for one plan-card event. Pure. */
export function planCardActivity(event: PlanCardEvent): PlanCardActivity {
  const { action, code, ...rest } = event;
  return {
    type: 'alfie',
    action,
    entity_type: 'plan_card',
    entity_id: code,
    entity_name: null,
    description: DESCRIPTIONS[action],
    metadata: { ...rest },
  };
}

/** Fire and forget. A tracking failure is not the member's problem. */
export function trackPlanCard(event: PlanCardEvent): void {
  try {
    void api.logActivity(planCardActivity(event)).catch(() => {});
  } catch {
    // logActivity never throws synchronously, but the cards must not depend on it.
  }
}
